/*
 * What a tab that is not being looked at says (spec 09 §3, A33). Three surfaces show the same
 * number — the tab's title, a dot on the favicon, and the platform's own badge where there is one —
 * and all three are driven from the client model, so they are right whether or not the Activity
 * page was ever opened.
 *
 * The count is handed to `core/shell/usePageTitle.ts` rather than written into `document.title`
 * here, because the screen that is open names the tab too and one string cannot have two writers:
 * this file owns the number, that one owns the sentence it stands in front of.
 *
 * The dot is drawn onto the application's own icon rather than onto a second file: the icon is
 * fetched once, a circle is added to it, and the result becomes a `data:` icon. If that fetch does
 * not come back the title still carries the count, which is the part that matters.
 */

import type { StoreApi } from "zustand"
import type { Platform } from "../../platform/types.ts"
import { webPlatform } from "../../platform/web.ts"
import { setTabWaiting } from "../shell/index.ts"
import type { Model } from "../store/model.ts"
import { unreadBadge } from "./feed.ts"

/** Where the icon of this build lives; the document links the same file. */
const ICON_PATH = "/icons/icon.svg"

/*
 * The dot: bottom right of the icon, in `--color-danger` (light) — the colour a notification wears
 * everywhere else. The value is written out rather than read from a token because it is spliced into
 * an SVG the browser is handed as a `data:` URL, where no stylesheet and no theme reaches it;
 * `indicator.test.ts` reads `tokens.css` and fails when the two drift apart.
 *
 * The four numbers follow the *icon's* viewBox and not a fixed grid: `icons/icon.svg` is the rocket
 * on its navy disc, 512 units square, so the dot sits three quarters across and down with a radius
 * of 21.9 % and a ring of 6.25 % — the proportions it had on the 64-unit placeholder this icon
 * replaced. An icon drawn on another viewBox needs them redrawn, which the test's quadrant
 * assertion is there to catch.
 */
const DOT = '<circle cx="384" cy="384" r="112" fill="#b62100" stroke="#ffffff" stroke-width="32"/>'

export interface IndicatorOptions {
	platform?: Platform
	document?: Document
}

/**
 * Follows the unread count and keeps title, favicon and platform badge on it. Returns the function
 * that stops it and puts the document back the way it was found.
 */
export function installUnreadIndicator(
	store: StoreApi<Model>,
	options: IndicatorOptions = {},
): () => void {
	const doc = options.document ?? globalThis.document
	const platform = options.platform ?? webPlatform
	const icon = doc.querySelector<HTMLLinkElement>('link[rel~="icon"]')
	const baseHref = icon?.getAttribute("href") ?? ICON_PATH
	let dotted: string | undefined
	let shown = -1

	const paint = (count: number): void => {
		if (count === shown) return
		shown = count
		setTabWaiting(count)
		if (icon) icon.setAttribute("href", count > 0 ? (dotted ?? baseHref) : baseHref)
		platform.setBadge(count)
	}

	// The dotted icon is built once, in the background; until it is there the title carries the
	// count on its own.
	void dottedIcon(baseHref).then((built) => {
		dotted = built
		if (shown > 0 && icon && built !== undefined) icon.setAttribute("href", built)
	})

	paint(unreadBadge(store.getState()))
	const stop = store.subscribe((model) => paint(unreadBadge(model)))
	return () => {
		stop()
		setTabWaiting(0)
		icon?.setAttribute("href", baseHref)
		platform.setBadge(0)
	}
}

/** The application's icon with a dot on it, as a `data:` URL, or undefined when it cannot be read. */
async function dottedIcon(href: string): Promise<string | undefined> {
	try {
		const response = await fetch(href, { cache: "force-cache" })
		if (!response.ok) return undefined
		const svg = await response.text()
		const end = svg.lastIndexOf("</svg>")
		if (end < 0) return undefined
		return `data:image/svg+xml,${encodeURIComponent(svg.slice(0, end) + DOT + "</svg>")}`
	} catch {
		// No icon to decorate — a blocked fetch, a document without one. The title still counts.
		return undefined
	}
}
